<template>
  <div class="filters-menu-container">
    <div v-if="loading">
      <loading-state />
    </div>
    <div class="filters-items">
      <div class="filters-list">
        <span class="filter-title">Filtros</span>
        <div class="filters-items-list">
          <div v-for="(filter, index) in filters" :key="index">
            <div v-if="filter === 'lojas'">
              <mf-select-units-dialog
                ref="mfSelectUnitsDialog"
                hide-details
                :selected-units.sync="localFilters.units"
                :selected-units-configuration.sync="localFilters.unitsConfiguration"
                :check-all-units-on-init="checkAllUnitsOnInit"
                :required="true"
                :units="units"
                :tag-list="tagList"
                :group-list="unitsGroups"
                :hide-ecommerce-configuration="hideEcommerceConfiguration"
                :filter-to-units="filterToUnits"
                enable-inactive-units-selection
                template-type="badge-filter"
                @change="confirmSelectUnits($event)"
                @ecommerce-config-changed="$emit('ecommerce-config-changed', $event)"
              />
            </div>
            <div v-else-if="filter === 'segmentos'">
              <segmentos-dialog
                :required="false"
                single-line
                outlined
                :selecionados.sync="selectedSegments"
                template-type="badge-button"
                call-warmup-segments
              />
            </div>
            <div v-else-if="filter === 'seletor-mes'">
              <month-range-picker :analysed-period.sync="localFilters.month" />
            </div>
            <div v-else-if="filter === 'industrias'">
              <select-options-new-filter
                v-model="localFilters.industries"
                label="Indústria"
                :options="industryNamesAndIds"
                item-text="name"
                item-value="_id"
                return-object
              />
            </div>
            <div v-else-if="filter === 'industria-mecanica'">
              <select-options-new-filter v-model="localFilters.mechanics" label="Mecânica" :options="mechanicOptions" />
            </div>
            <div v-else-if="filter === 'industria-publico-alvo'">
              <select-options-new-filter v-model="localFilters.segmentType" label="Público-alvo" :options="segmentTypeOptions" />
            </div>
            <div v-else-if="filter === 'industria-rebate'">
              <select-options-new-filter v-model="localFilters.rebateStatus" label="Apuração do reembolso" :options="rebateStatusOptions" />
            </div>
          </div>
        </div>
      </div>
      <div class="filters-items-action-buttons">
        <mf-button
          v-if="clearButtonVisibility"
          label="Limpar"
          text
          icon="filter_alt_off"
          iconPosition="right"
          fill-icon
          text-transform="none"
          size="md"
          @click="handleClear"
        />
        <mf-button id="filter_button" label="Filtrar" primary class="spacing" size="md" depressed text-transform="none" @click="handleFilter" />
      </div>
    </div>
    <mf-info-card
      v-if="showOutdatedWarning && outdatedFilters"
      class="mt-4"
      description="Exibindo resultados desatualizados de acordo com seus novos filtros. Clique em Filtrar para atualizar os resultados."
      icon="warning"
      variant="warning"
    />
  </div>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex'
import LoadingState from './LoadingState.vue'
import SegmentosDialog from '../../modules/campanhas/views/components/SegmentosDialog.vue'
import SelectOptionsNewFilter from './SelectOptionsNewFilter.vue'
import { DateTime } from 'luxon'
import MonthRangePicker from '@/modules/empresa/dre/views/components/MonthRangePicker.vue'

export default {
  name: 'NewFiltersMenu',
  components: {
    LoadingState,
    SegmentosDialog,
    SelectOptionsNewFilter,
    MonthRangePicker,
  },
  props: {
    filters: {
      type: Array,
      default: () => [],
      validator: (value) =>
        ['lojas', 'segmentos', 'seletor-mes', 'industrias', 'industria-mecanica', 'industria-publico-alvo', 'industria-rebate'].includes(...value),
    },
    filterToUnits: {
      type: Array,
      default: () => [],
    },
    checkAllUnitsOnInit: {
      type: Boolean,
      default: true,
    },
    hideEcommerceConfiguration: {
      type: Boolean,
      default: false,
    },
    clearFilterOnInit: {
      type: Boolean,
      default: false,
    },
    showOutdatedWarning: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    loading: false,
    clearButtonVisibility: true,
    localSelectedSegments: [],
    localSelectedUnits: [],
    localFilters: {
      units: [],
      unitsConfiguration: [],
      segments: [],
      segmentsObjects: [],
      industries: [],
      mechanics: [],
      month: DateTime.now().toFormat('yyyy-MM'),
      rebateStatus: [],
      segmentType: [],
    },
  }),
  computed: {
    ...mapState({ stateFilters: (state) => state.filters }),
    ...mapGetters(['units', 'tagList', 'getQuickAction', 'unitsGroups', 'industryNamesAndIds']),
    selectedSegments: {
      get() {
        return this.localSelectedSegments
      },
      set(value) {
        if (value) {
          this.localSelectedSegments = value
          this.localFilters.segments = value.map((segment) => segment._id)
          this.localFilters.segmentsObjects = value
        }
      },
    },
    paramFilters() {
      const paramFilters = {
        ...this.stateFilters,
        units: this.checkFilters(this.localFilters.units && this.localFilters.units.length === this.units.length ? [] : this.localFilters.units),
        unitsConfiguration: this.checkFilters(this.localFilters.unitsConfiguration),
        segments: this.checkFilters(this.localFilters.segments?.length ? this.localFilters.segments : []),
        segmentsObjects: this.checkFilters(this.localFilters.segmentsObjects?.length ? this.localFilters.segmentsObjects : []),
        industries: this.checkFilters(this.localFilters.industries),
        mechanics: this.checkFilters(this.localFilters.mechanics),
        month: this.localFilters.month ? this.localFilters.month : DateTime.now().toFormat('yyyy-MM'),
        rebateStatus: this.checkFilters(this.localFilters.rebateStatus),
        segmentType: this.checkFilters(this.localFilters.segmentType),
      }

      return paramFilters
    },

    rebateStatusOptions() {
      return [
        {
          text: 'Em apuração',
          value: 'counting',
        },
        {
          text: 'Apuração concluída',
          value: 'counted',
        },
      ]
    },

    segmentTypeOptions() {
      return [
        {
          text: 'Massivo',
          value: 'massive',
        },
        {
          text: 'Clube',
          value: 'club',
        },
        {
          text: 'Personalizado',
          value: 'personalized',
        },
      ]
    },

    mechanicOptions() {
      return [
        {
          text: 'Desconto absoluto',
          value: 'general',
        },
        {
          text: 'Desconto percentual',
          value: 'percentage',
        },
        {
          text: 'Leve e pague',
          value: 'takepay',
        },
        {
          text: 'Cashback',
          value: 'nextbuycashback',
        },
        {
          text: 'Compre e ganhe',
          value: 'dynamic_rule',
        },
        {
          text: 'Cross sell',
          value: 'crosssell',
        },
        {
          text: 'Zé Compensa',
          value: 'benefits',
        },
      ]
    },

    selectedFiltersMap() {
      const selectedFilters = []
      this.filters.forEach((filter) => {
        switch (filter) {
          case 'lojas':
            selectedFilters.push('units')
            break
          case 'segmentos':
            selectedFilters.push('segments')
            break
          case 'seletor-mes':
            selectedFilters.push('month')
            break
          case 'industrias':
            selectedFilters.push('industries')
            break
          case 'industria-mecanica':
            selectedFilters.push('mechanics')
            break
          case 'industria-publico-alvo':
            selectedFilters.push('segmentType')
            break
          case 'industria-rebate':
            selectedFilters.push('rebateStatus')
            break
          default:
            break
        }
      })
      return selectedFilters
    },

    outdatedFilters() {
      let outdated = false
      this.selectedFiltersMap.forEach((filter) => {
        if (JSON.stringify(this.stateFilters[filter]) !== JSON.stringify(this.localFilters[filter])) {
          outdated = true
        }
      })

      return outdated
    },
  },
  watch: {
    localFilters: {
      deep: true,
      handler() {
        this.checkClearFilters(this.localFilters)
      },
    },
  },
  mounted() {
    this.$nextTick(() => {
      const quickAction = Boolean(this.$route.query.quick_action)
      if (quickAction) {
        this.prebuildQuickAction()
        this.$bus.$emit('filter', { isInitialLoad: false })
      } else if (this.clearFilterOnInit) {
        this.handleClear()
      } else {
        this.fillFilters()
        this.putFiltersOnQueryParam(true)
      }
    })
  },
  methods: {
    ...mapActions(['setFilters', 'setIsIncludingSegments']),
    checkFilters(filter) {
      if (filter === '' || filter === undefined) return null
      return filter
    },
    fillFilters() {
      this.selectedSegments = this.stateFilters.segmentsObjects
      this.localFilters.units = this.stateFilters.units
      this.localFilters.unitsConfiguration = this.stateFilters.unitsConfiguration
      this.localFilters.industries = this.stateFilters.industries
      this.localFilters.mechanics = this.stateFilters.mechanics
      this.localFilters.month = this.stateFilters.month
      this.localFilters.rebateStatus = this.stateFilters.rebateStatus
      this.localFilters.segmentType = this.stateFilters.segmentType
    },
    confirmSelectUnits(isAllUnitsSelected) {
      this.$emit('confirm-select-units', isAllUnitsSelected)
    },
    checkClearFilters(object) {
      for (let key in object) {
        if (object.hasOwnProperty(key)) {
          if (typeof object[key] === 'object') {
            this.checkClearFilters(object[key])
          } else if (object[key]?.length > 0) {
            this.clearButtonVisibility = true
          }
        }
      }
    },
    handleClear() {
      this.localFilters = {
        units: [],
        unitsConfiguration: [],
        segments: [],
        segmentsObjects: [],
        industries: [],
        mechanics: [],
        month: DateTime.now().toFormat('yyyy-MM'),
        rebateStatus: [],
        segmentType: [],
      }
      this.selectedSegments = []
      this.putFiltersOnQueryParam()
    },
    handleFilter() {
      this.putFiltersOnQueryParam()
    },
    putFiltersOnQueryParam(isInitialLoad = false) {
      // Unset quick actions when changing the filter
      this.setIsIncludingSegments(false)
      this.setFilters(this.paramFilters)

      this.$nextTick(() => {
        this.$bus.$emit('filter', { isInitialLoad })
      })
    },
    async prebuildQuickAction() {
      if (!this.getIsQuickAction() || !this.getQuickAction?.segmentation) return
      this.selectedSegments = [this.getQuickAction.segmentation.segment]
      this.putFiltersOnQueryParam()
      this.unsetQuickAction()
    },
  },
}
</script>

<style lang="scss">
.filters-menu-container {
  width: 100%;
  margin-bottom: 10px;
  .filters-items {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    border-bottom: solid 1px $marca-0-l20;
    min-height: 80px;
    .filters-list {
      display: flex;
      flex-direction: column;
      gap: 12px;
      .filters-items-list {
        display: flex;
        flex-wrap: wrap;
        gap: 16px;
        align-items: center;
      }
      .filter-title {
        color: $marca-0;
        font-size: 16px;
        font-weight: 500;
        line-height: 120%;
        letter-spacing: 0.4px;
      }
    }
    .filters-items-action-buttons {
      display: flex;
      flex-direction: row;
      justify-content: flex-end;
      gap: 8px;
      margin-top: 32px;
    }
  }
}
</style>
